/**
 * Lifecycle colour system.
 *
 * PALETTE PROVENANCE
 * ------------------
 * These are the six lifecycle groups the API returns. Colour is assigned by
 * *state*, never by rank or by position in a list, so filtering the chart never
 * repaints the surviving bars.
 *
 * Both modes were checked with the dataviz palette validator, passing the five
 * chromatic slots in the order they appear in the chart (the validator scores
 * adjacent pairs, and chart order is what a reader actually compares):
 *
 *   light  #eda100,#2a78d6,#0ca30c,#4a3aa7,#d03b3b  on #ffffff
 *     [PASS] lightness band · [PASS] chroma floor
 *     [PASS] CVD separation      worst adjacent ΔE 21.5 (protan)
 *     [PASS] normal-vision floor worst adjacent ΔE 30.8
 *     [WARN] contrast            #eda100 at 2.17:1
 *
 *   dark   #c98500,#3987e5,#0ca30c,#9085e9,#e66767  on #141416
 *     ALL CHECKS PASS
 *
 * The single contrast WARN on the amber "pending" step obligates relief: every
 * place that colour appears also carries a visible text label (chart bars are
 * direct-labelled; badges pair the dot with the status name). Colour therefore
 * never carries meaning on its own anywhere in this UI.
 *
 * An earlier candidate used #ec835a for `exception`; the validator failed it at
 * ΔE 5.6 against #0ca30c under protanopia, so it was re-stepped to violet.
 */

export type LifecycleGroup =
  | 'pending_pickup'
  | 'in_transit'
  | 'delivered'
  | 'returned'
  | 'exception'
  | 'cancelled'
  | 'unknown';

interface GroupStyle {
  /** Tailwind token name, resolved from the CSS custom properties. */
  token: string;
  /** Solid fill for chart marks and badge dots. */
  fill: string;
  /** Tint used behind badges. */
  chipClass: string;
  /** Text colour for badges. */
  textClass: string;
  label: string;
}

export const GROUP_STYLES: Record<LifecycleGroup, GroupStyle> = {
  pending_pickup: {
    token: 'life-pending',
    fill: 'var(--life-pending)',
    chipClass: 'bg-life-pending/12 ring-life-pending/30',
    textClass: 'text-ink-primary',
    label: 'Awaiting pickup',
  },
  in_transit: {
    token: 'life-transit',
    fill: 'var(--life-transit)',
    chipClass: 'bg-life-transit/12 ring-life-transit/30',
    textClass: 'text-ink-primary',
    label: 'In transit',
  },
  delivered: {
    token: 'life-delivered',
    fill: 'var(--life-delivered)',
    chipClass: 'bg-life-delivered/12 ring-life-delivered/30',
    textClass: 'text-ink-primary',
    label: 'Delivered',
  },
  exception: {
    token: 'life-exception',
    fill: 'var(--life-exception)',
    chipClass: 'bg-life-exception/12 ring-life-exception/30',
    textClass: 'text-ink-primary',
    label: 'Exception',
  },
  returned: {
    token: 'life-returned',
    fill: 'var(--life-returned)',
    chipClass: 'bg-life-returned/12 ring-life-returned/30',
    textClass: 'text-ink-primary',
    label: 'Returned',
  },
  cancelled: {
    token: 'life-cancelled',
    fill: 'var(--life-cancelled)',
    chipClass: 'bg-life-cancelled/12 ring-life-cancelled/30',
    textClass: 'text-ink-secondary',
    label: 'Cancelled',
  },
  unknown: {
    token: 'life-cancelled',
    fill: 'var(--life-cancelled)',
    chipClass: 'bg-life-cancelled/12 ring-life-cancelled/30',
    textClass: 'text-ink-secondary',
    label: 'Unclassified',
  },
};

export function groupStyle(group: string): GroupStyle {
  return GROUP_STYLES[(group as LifecycleGroup) in GROUP_STYLES ? (group as LifecycleGroup) : 'unknown'];
}
